import React, { useEffect, useReducer, useCallback, useMemo } from 'react'
import {
  Typography,
  Button,
  ButtonBase,
  Tooltip,
  Fab,
  Grid,
  LinearProgress,
  IconButton
} from '@material-ui/core'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import InfoIcon from '@material-ui/icons/Info'
import IconStore from '@material-ui/icons/Store'
import IconLocation from '@material-ui/icons/LocationOn'
import IconDirections from '@material-ui/icons/Directions'
import IconVerifiedUser from '@material-ui/icons/VerifiedUser'
import ArrowBack from '@material-ui/icons/ArrowBack'

import Logo from '~/assets/logo-map.svg'
import notFoundIcon from '~/assets/box.svg'
import PhoneNumber from '~/components/PhoneNumber'
import stockIcon from '~/assets/stock.svg'
import DrawerProduct from '~/components/DrawerProduct'
import { useLiterals } from '~/components/Literals'
import StoreProductsGrid from './StoreProductsGrid'
import api from '~/api'
import track from '~/track'
import useStyles from './storeDetailsStyles'
import { getExtraInfoByBrand } from '~/components/StoreList/helper'
import { getElasticFieldNameByLanguage } from '~/utils'

function selectStore(state) {
  return { cnpj: state.app.storeAlone }
}

const initialState = {
  loadingProducts: true,
  loadingDetails: true,
  products: null,
  store: null
}

function reducer(state, action) {
  return { ...state, ...action }
}

function sortProducts(items = [], language = '') {
  return items
    .sort((a, b) => {
      if (a.brand && b.brand) return a.brand - b.brand
      return 0
    })
    .sort((a, b) => {
      if (a.brand === b.brand) {
        const nameA = a[getElasticFieldNameByLanguage(language, 'name')]
        const nameB = b[getElasticFieldNameByLanguage(language, 'name')]

        if (nameA.toLowerCase() < nameB.toLowerCase()) {
          return -1
        }

        if (nameA.toLowerCase() > nameB.toLowerCase()) {
          return 1
        }

        return 0
      }
    })
}

function clickTrack({ data, position }) {
  api.clickTrack({
    ...data,
    user: position?.user
  })
}

function StoreDetailsAlone({ config, position }) {
  const history = useHistory()
  const classes = useStyles()
  const [state, dispatch] = useReducer(reducer, initialState)
  const { cnpj } = useSelector(selectStore)
  const { getLiteral } = useLiterals()
  const { drawerProduct, setDrawerProduct, closeDrawerProduct } = useDrawerProduct()
  const showBackButton = position.fetched
  const { store, products, loadingProducts, loadingDetails } = state
  const { brandName, hideClaimStore, language } = config
  const handleBackButton = () => history.goBack()
  const extraInfo = useMemo(
    () => store?.extra_info && getExtraInfoByBrand(store.extra_info, config.cnpj),
    [store, config.cnpj]
  )

  const logDefault = useMemo(
    () =>
      store && {
        gtin: 'all',
        productName: 'todos',
        name: store.name,
        cnpj: store.cnpj
      },
    [store]
  )

  const handleClaim = useCallback(() => {
    window.open('https://varejo.gofind.com.br/')
    track('reivindicarLoja', { name: store.name, category: 'loja' })
    clickTrack({ ...logDefault, event: 'claim', position })
  }, [logDefault])

  const handleSeePhone = (type) => clickTrack({ ...logDefault, event: 'call', type, position })

  const handleClickRoute = useCallback(() => {
    track('cliqueRota', { name: store.name, category: 'loja' })
    window.open(`http://maps.google.com/?q=${store.formatted_address}`)
    clickTrack({ ...logDefault, event: 'route', position })
  }, [store, logDefault])

  const handleProductClick = useCallback(
    (product) => {
      setDialogProduct({ product })
      clickTrack({
        ...logDefault,
        event: 'product',
        gtin: product.gtin,
        productName: product.name,
        brand: config.isOndetem ? product.brand : null,
        position
      })
    },
    [store]
  )

  useEffect(() => {
    // @todo: tratar error
    async function getDetails() {
      const response = await api.getStoreDetails(cnpj)
      dispatch({ store: response.store, loadingDetails: false })
    }

    // @todo: tratar error
    async function getProducts() {
      const response = await api.getStoreProducts(cnpj)
      dispatch({
        products: sortProducts(response.products, language),
        loadingProducts: false
      })
    }

    getDetails()
    getProducts()
  }, [cnpj])

  if (loadingDetails) {
    return <LinearProgress />
  }

  if (!store) {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justify="center"
        className={classes.notFound}
      >
        <Grid item>
          <Typography variant="h2">{getLiteral('1657')}</Typography>
          <br />
          <img src={notFoundIcon} alt={getLiteral('2093')} className={classes.imgNotFound} />
          <br />

          <Button variant="contained" color="primary" component={Link} fullWidth to="/">
            {getLiteral('2094')}
          </Button>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.root}>
      <DrawerProduct
        data={drawerProduct}
        position={position}
        onClose={closeDrawerProduct}
        noRedirect
      />
      <div className={classes.gridHeader}>
        <Grid container spacing={2} className={classes.headerWrapper}>
          {store.logo && (
            <Grid item>
              <img src={store.logo} alt="product" className={classes.logoImage} />
            </Grid>
          )}
          <Grid item style={{ flex: '1', width: '100%' }}>
            <Typography variant="h5" color="inherit" gutterBottom noWrap>
              {showBackButton && (
                <Tooltip
                  id="tooltip-back"
                  title={getLiteral('1051')}
                  placement="bottom-end"
                  className={classes.return}
                  PopperProps={{
                    container: document.getElementById('gofind-finder') || document.body
                  }}
                >
                  <IconButton
                    color="inherit"
                    className={classes.returnButton}
                    onClick={handleBackButton}
                    aria-label="go back"
                  >
                    <ArrowBack />
                  </IconButton>
                </Tooltip>
              )}
              {store.name}
            </Typography>

            {store.formatted_address && (
              <div className={classes.detail}>
                <IconLocation />
                <Typography variant="body1" color="inherit">
                  {store.formatted_address.replace(', Brazil', '')}
                </Typography>
              </div>
            )}

            {extraInfo && (
              <div className={classes.detail}>
                <InfoIcon />
                <Typography variant="body1" color="inherit">
                  {extraInfo}
                </Typography>
              </div>
            )}

            {store.phone_number && store.whatsapp && (
              <PhoneNumber
                whatsapp={store.whatsapp}
                phoneNumber={store.phone_number}
                onSeePhone={handleSeePhone}
                storeName={store.name}
              />
            )}

            {!store.verified ? (
              !hideClaimStore && (
                <ButtonBase
                  component="a"
                  onClick={handleClaim}
                  data-cy="claim"
                  className={cx(classes.detail, classes.phone)}
                >
                  <IconStore />
                  <Typography variant="body1" color="inherit">
                    {getLiteral('2004')}
                  </Typography>
                </ButtonBase>
              )
            ) : (
              <div className={classes.detail}>
                <IconVerifiedUser />
                <Typography variant="body1" color="inherit">
                  {getLiteral('2005')}
                </Typography>
              </div>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.headerTitle}>
          <div className={classes.headerTitleContent}>
            {brandName !== 'Gofind Vitrine' ? (
              <Typography className={classes.headerTitleTypo} variant="h5">
                {getLiteral('2008', [brandName])}
              </Typography>
            ) : (
              <Typography className={classes.headerTitleTypo} variant="h5">
                {getLiteral('1183')}
              </Typography>
            )}
          </div>
        </Grid>
      </div>
      <Grid item xs={12} className={classes.gridBody}>
        {loadingProducts ? (
          <LinearProgress />
        ) : (
          <>
            {products && products.length ? (
              <StoreProductsGrid
                products={products}
                language={language}
                onProductClick={handleProductClick}
                classes={classes}
              />
            ) : (
              <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justify="center"
                className={classes.notFoundProducts}
              >
                <Grid item>
                  <Typography variant="h4" component="h1">
                    {getLiteral('2009', [brandName])}
                  </Typography>
                  <br />
                  <img src={stockIcon} alt={getLiteral('2095')} className={classes.imgNotFound} />
                  <br />
                  <br />
                  <Button variant="contained" color="primary" size="large" component={Link} to="/">
                    {getLiteral('2094')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Grid container className={classes.gridFooter}>
        <Grid container item xs={12} alignContent="center" alignItems="center">
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.gofind.online/pertinhodemim?utm_source=Pertinhodemim%20Logo&utm_medium=mapa&utm_campaign=logo%20map"
            className={classes.logoGofind}
          >
            <img src={Logo} alt={getLiteral('2096')} />
          </a>
        </Grid>
        <div className={classes.buttonWrap}>
          <Tooltip
            title={getLiteral('1479')}
            PopperProps={{ container: document.getElementById('gofind-finder') || document.body }}
          >
            <Fab
              data-cy="route"
              className={classes.buttonRoute}
              onClick={handleClickRoute}
              color="primary"
              disabled={false}
            >
              <IconDirections />
            </Fab>
          </Tooltip>
        </div>
      </Grid>
    </div>
  )
}

export default StoreDetailsAlone
